import { useQuery } from "@tanstack/react-query";

import {
  fetchIntegrationSettings,
  fetchBrowserStackDevices,
  fetchBrowserStackBrowsers,
  fetchSauceLabsDevices,
  fetchSauceLabsBrowsers,
  fetchLambdaTestDevices,
  fetchAwsDeviceFarmDevices,
  fetchMobileProviderCapabilities,
  checkEmptyElement,
  checkURLAccess,
  fetchAgents,
  fetchAdhocRunConfig,
  fetchEnvironment,
  fetchTestCaseResults,
  fetchTestCaseResultById,
  fetchTestStepResults,
  fetchTestStepResultById,
  fetchDataDrivenResults,
  fetchRunHistory,
  fetchFlakyScore,
  fetchChildTestCaseResults,
} from "./test-run.api";

import type { ResultSource } from "@/common/services/result-source";
import type { GenericQueryParams } from "@/common/types/TestCase";

// Already present
export const useIntegrationSettingsQuery = ({
  enabled = true,
}: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: ["integrationSettings"],
    queryFn: fetchIntegrationSettings,
    staleTime: 5 * 60 * 1000, // 5 min - rarely changes
    enabled,
  });

export const useBrowserStackDeviceQuery = (
  configId?: number | string,
  platform?: string,
) =>
  useQuery({
    queryKey: ["browserStackDevices", configId, platform],
    queryFn: async () => {
      const response = await fetchBrowserStackDevices(configId!, platform);
      return response.data ?? [];
    },
    enabled: !!configId,
    staleTime: 10 * 60 * 1000,
  });

export const useBrowserStackBrowserQuery = (
  configId?: number | string,
  filters?: {
    platform?: string;
    device?: string;
    osVersion?: string;
    browser?: string;
  },
) =>
  useQuery({
    queryKey: ["browserStackBrowsers", configId, filters],
    queryFn: async () => {
      const response = await fetchBrowserStackBrowsers(configId!, filters);
      return response.data ?? [];
    },
    enabled: !!configId && (filters === undefined || Boolean(filters.device)),
    staleTime: 10 * 60 * 1000,
  });

export const useSauceLabsDeviceQuery = (
  configId?: number | string,
  platform?: string,
) =>
  useQuery({
    queryKey: ["sauceLabsDevices", configId, platform],
    queryFn: async () => {
      const response = await fetchSauceLabsDevices(configId!, platform);
      return response.data ?? [];
    },
    enabled: !!configId,
    staleTime: 10 * 60 * 1000,
  });

export const useAwsDeviceFarmDeviceQuery = (
  configId?: number | string,
  platform?: string,
) =>
  useQuery({
    queryKey: ["awsDeviceFarmDevices", configId, platform],
    queryFn: async () => {
      const response = await fetchAwsDeviceFarmDevices(configId!, platform);
      return response.data ?? [];
    },
    enabled: !!configId,
    staleTime: 10 * 60 * 1000,
  });

export const useLambdaTestDeviceQuery = (
  configId?: number | string,
  platform?: string,
) =>
  useQuery({
    queryKey: ["lambdaTestDevices", configId, platform],
    queryFn: async () => {
      const response = await fetchLambdaTestDevices(configId!, platform);
      return response.data ?? [];
    },
    enabled: !!configId,
    staleTime: 10 * 60 * 1000,
  });

export const useMobileProviderCapabilitiesQuery = (
  configId?: number | string,
  enabled = true,
) =>
  useQuery({
    queryKey: ["mobileProviderCapabilities", configId],
    queryFn: async () => {
      const response = await fetchMobileProviderCapabilities(configId!);
      return response.data?.data ?? null;
    },
    enabled: !!configId && enabled,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });

export const useSauceLabsBrowserQuery = (
  configId?: number | string,
  filters?: {
    platform?: string;
    device?: string;
    osVersion?: string;
    browser?: string;
  },
) =>
  useQuery({
    queryKey: ["sauceLabsBrowsers", configId, filters],
    queryFn: async () => {
      const response = await fetchSauceLabsBrowsers(configId!, filters);
      return response.data ?? [];
    },
    enabled: !!configId && (filters === undefined || Boolean(filters.device)),
    staleTime: 10 * 60 * 1000,
  });

// For checkEmptyElement
export const useCheckEmptyElementQuery = (
  testCaseId: number | string,
  versionId: number | string,
) =>
  useQuery({
    queryKey: ["checkEmptyElement", testCaseId, versionId],
    queryFn: () => checkEmptyElement(testCaseId, versionId),
    enabled: !!testCaseId && !!versionId,
    staleTime: 60 * 1000, // 1 min
  });

// For checkURLAccess
export const useCheckURLAccessQuery = (testCaseId: number | string) =>
  useQuery({
    queryKey: ["checkURLAccess", testCaseId],
    queryFn: () => checkURLAccess(testCaseId),
    enabled: !!testCaseId,
    staleTime: 60 * 1000, // 1 min
  });

// For fetchAgents
export const useAgentsQuery = (query?: string) =>
  useQuery({
    queryKey: ["agents", query],
    queryFn: () => fetchAgents(query),
    staleTime: 5 * 60 * 1000, // 5 min - rarely changes
  });

// For fetchAdhocRunConfig
export const useAdhocRunConfigQuery = (applicationType: string) =>
  useQuery({
    queryKey: ["adhocRunConfig", applicationType],
    queryFn: () => fetchAdhocRunConfig(applicationType),
    enabled: !!applicationType,
  });

// For fetchEnvironment
export const useEnvironmentQuery = () =>
  useQuery({
    queryKey: ["environments"],
    queryFn: fetchEnvironment,
    staleTime: 5 * 60 * 1000, // 5 min - rarely changes
  });

export const useTestCaseResults = (params: GenericQueryParams) =>
  useQuery({
    queryKey: ["testCaseResults", params],
    queryFn: () => fetchTestCaseResults(params),
    enabled: !!params,
  });

export const useTestCaseResultById = (
  testCaseResultId: number,
  refetchInterval?: (data: any) => number | false,
  source: ResultSource = "plan",
) =>
  useQuery({
    queryKey: ["testCaseResultById", testCaseResultId, source],
    queryFn: () => fetchTestCaseResultById(testCaseResultId, source),
    enabled: !!testCaseResultId,
    refetchInterval,
    staleTime: 30 * 1000, // 30s — reuse cached result without refetching
  });

export const useTestStepResults = (
  testCaseResultId: number,
  refetchInterval?: (data: any) => number | false,
  source: ResultSource = "plan",
) =>
  useQuery({
    queryKey: ["testStepResults", testCaseResultId, source],
    queryFn: () => fetchTestStepResults(testCaseResultId, source),
    enabled: !!testCaseResultId,
    refetchInterval,
    staleTime: 30 * 1000, // 30s — reuse cached step results without refetching
  });

export const useTestStepResultById = (
  testStepId: number | string,
  source: ResultSource = "plan",
) =>
  useQuery({
    queryKey: ["testStepResultById", testStepId, source],
    queryFn: () => fetchTestStepResultById(testStepId, source),
    enabled: !!testStepId,
    staleTime: 30 * 1000, // 30s — reuse prefetched data from hover without refetching
  });

export const useTestDataDrivenResults = (
  testCaseResultId: number | string,
  source: ResultSource = "plan",
) =>
  useQuery({
    queryKey: ["testDataDrivenResults", testCaseResultId, source],
    queryFn: () => fetchDataDrivenResults(testCaseResultId, source),
    enabled: !!testCaseResultId,
  });
export const useChildTestCaseResults = (
  parentId: number | null,
  refetchInterval?: (data: any) => number | false,
  source: ResultSource = "plan",
) =>
  useQuery({
    queryKey: ["childTestCaseResults", parentId, source],
    queryFn: () => fetchChildTestCaseResults(parentId!, source),
    enabled: !!parentId,
    refetchInterval,
  });
export const useRunHistory = (
  testCaseId: number | string,
  limit = 10,
  source: ResultSource = "plan",
) =>
  useQuery({
    queryKey: ["runHistory", testCaseId, limit, source],
    queryFn: () => fetchRunHistory(testCaseId, limit, source),
    enabled: !!testCaseId,
    staleTime: 30 * 1000, // 30s
  });

// Phase 4: Flaky Score
export const useFlakyScore = (
  testCaseId: number | string,
  window = 10,
  source: ResultSource = "plan",
) =>
  useQuery({
    queryKey: ["flakyScore", testCaseId, window, source],
    queryFn: () => fetchFlakyScore(testCaseId, window, source),
    enabled: !!testCaseId,
    staleTime: 60 * 1000, // 1min
  });
